import { Box, Tab, Tabs, Typography } from "@mui/material"
import type { LineSeries } from "@mui/x-charts-pro"
import { useMemo, useState } from "react"
import { extraColors } from "../../../../utils/theme"
import type { BaseLegendItemData } from "../../../Charts/legendUtils"
import { TimeSeriesChart } from "../../../Charts/TimeSeriesChart"
import type { DatasetEntry } from "../../timeSeriesConstants"
import { DataSourceCard } from "../DataSourceCard"

import type { DateRange } from "@mui/x-date-pickers-pro"
import { useListHawaiiCovidHawaiiCovidListGet } from "@phc/common/gen/threatAnalyticsGen"
import type { Dayjs } from "dayjs"
import { dayjsUTC } from "../../../../utils/dates"
import { ChartWithFilters } from "../../../Charts/ChartWithFilters"
import { GenericTag } from "../../../Shared/GenericTag"
import { HawaiiCovidTooltipBody } from "./HawaiiCovidTimeSeriesTooltip"
import { useHawaiiCovidDataTransform } from "./useHawaiiCovidDataTransform"

export function isHawaiiCovidKey(
  key: string
): key is
  | "weekly_cases"
  | "rolling_avg_cases_last_7_days"
  | "weekly_test_positivity"
  | "rolling_avg_test_positivity_last_7_days" {
  return (
    key === "weekly_cases" ||
    key === "rolling_avg_cases_last_7_days" ||
    key === "weekly_test_positivity" ||
    key === "rolling_avg_test_positivity_last_7_days"
  )
}

export interface HawaiiCovidChartData {
  dataset: DatasetEntry[]
  dates: Date[]
  casesChart: {
    series: LineSeries[]
    legendItems: BaseLegendItemData[]
  }
  positivityChart: {
    series: LineSeries[]
    legendItems: BaseLegendItemData[]
  }
}

const HawaiiCovidErrorState = () => (
  <Box>
    <Typography variant="body2" color={extraColors.medium}>
      Error loading Hawaii COVID data
    </Typography>
  </Box>
)

const HawaiiCovidLoadingState = () => <Box>Loading...</Box>

type DataViewMode = "weekly" | "rolling_avg"

const CASES_WEEKLY_ID = "weekly_cases"
const CASES_ROLLING_AVG_ID = "rolling_avg_cases_last_7_days"
const POSITIVITY_WEEKLY_ID = "weekly_test_positivity"
const POSITIVITY_ROLLING_AVG_ID = "rolling_avg_test_positivity_last_7_days"

interface HawaiiCovidChartProps {
  county: string
}

const HawaiiCovidCasesChart = ({ county }: HawaiiCovidChartProps) => {
  const defaultDateRange = useMemo((): DateRange<Dayjs> => {
    const today = dayjsUTC()
    const sixMonthsAgo = today.subtract(6, "month")
    return [dayjsUTC(sixMonthsAgo), dayjsUTC(today)]
  }, [])
  const [covidCasesDateRange, setCovidCasesDateRange] =
    useState<DateRange<Dayjs>>(defaultDateRange)

  const {
    data: hawaiiCovidData,
    isLoading,
    isError,
  } = useListHawaiiCovidHawaiiCovidListGet({
    limit: 1000,
    county,
    week_ended_at_end: covidCasesDateRange[1]?.format("YYYY-MM-DD"),
    week_ended_at_start: covidCasesDateRange[0]?.format("YYYY-MM-DD"),
  })
  const { casesChart, dataset, dates } =
    useHawaiiCovidDataTransform(hawaiiCovidData)

  const [dataViewMode, setDataViewMode] = useState<DataViewMode>("weekly")

  const activeSeriesId =
    dataViewMode === "weekly" ? CASES_WEEKLY_ID : CASES_ROLLING_AVG_ID
  const filteredSeries = useMemo(
    () => casesChart.series.filter(s => s.id === activeSeriesId),
    [casesChart, activeSeriesId]
  )
  const filteredLegendItems = useMemo(
    () => casesChart.legendItems.filter(item => item.id === activeSeriesId),
    [casesChart.legendItems, activeSeriesId]
  )

  const errorState = isError ? <HawaiiCovidErrorState /> : null
  const loadingState = isLoading ? <HawaiiCovidLoadingState /> : null

  const chart = useMemo(() => {
    return (
      <TimeSeriesChart
        dataset={dataset}
        dates={dates}
        series={filteredSeries}
        legendItems={filteredLegendItems}
        yAxisLabel="CASE COUNT"
        renderLegend={() => null}
        renderTooltip={hiddenIds => (
          <HawaiiCovidTooltipBody
            rawData={hawaiiCovidData}
            hiddenIds={hiddenIds}
            itemIds={[
              dataViewMode === "weekly"
                ? CASES_WEEKLY_ID
                : CASES_ROLLING_AVG_ID,
            ]}
          />
        )}
      />
    )
  }, [
    dataset,
    dates,
    filteredSeries,
    filteredLegendItems,
    hawaiiCovidData,
    dataViewMode,
  ])

  return (
    <Box>
      <ChartWithFilters
        title="Covid Cases"
        tooltipContent={<HDOHDataSourceCard />}
        dataSourcePill={<GenericTag text="Honolulu County" />}
        filters={{
          dateRange: covidCasesDateRange,
          onDateRangeChange: setCovidCasesDateRange,
          tabs: (
            <Tabs
              value={dataViewMode}
              onChange={(_, value: DataViewMode) => setDataViewMode(value)}
            >
              <Tab label="Weekly" value="weekly" />
              <Tab label="7-day rolling average" value="rolling_avg" />
            </Tabs>
          ),
        }}
      >
        {isError ? errorState : loadingState ? loadingState : chart}
      </ChartWithFilters>
    </Box>
  )
}

const HawaiiCovidPositivityChart = ({ county }: HawaiiCovidChartProps) => {
  // default to 6mos ago to today
  const defaultDateRange = useMemo((): DateRange<Dayjs> => {
    const today = dayjsUTC()
    const sixMonthsAgo = today.subtract(6, "month")
    return [dayjsUTC(sixMonthsAgo), dayjsUTC(today)]
  }, [])

  const [covidPositivityDateRange, setCovidPositivityDateRange] =
    useState<DateRange<Dayjs>>(defaultDateRange)
  const {
    data: hawaiiCovidData,
    isLoading,
    isError,
  } = useListHawaiiCovidHawaiiCovidListGet({
    limit: 1000,
    county,
    week_ended_at_end: covidPositivityDateRange[1]?.format("YYYY-MM-DD"),
    week_ended_at_start: covidPositivityDateRange[0]?.format("YYYY-MM-DD"),
  })
  const { positivityChart, dataset, dates } =
    useHawaiiCovidDataTransform(hawaiiCovidData)

  const [dataViewMode, setDataViewMode] = useState<DataViewMode>("weekly")

  const activeSeriesId =
    dataViewMode === "weekly" ? POSITIVITY_WEEKLY_ID : POSITIVITY_ROLLING_AVG_ID
  const filteredSeries = useMemo(
    () => positivityChart.series.filter(s => s.id === activeSeriesId),
    [positivityChart.series, activeSeriesId]
  )
  const filteredLegendItems = useMemo(
    () =>
      positivityChart.legendItems.filter(item => item.id === activeSeriesId),
    [positivityChart.legendItems, activeSeriesId]
  )

  const errorState = isError ? <HawaiiCovidErrorState /> : null
  const loadingState = isLoading ? <HawaiiCovidLoadingState /> : null

  const chart = useMemo(() => {
    return (
      <ChartWithFilters
        title="Covid Test Positivity"
        tooltipContent={<HDOHDataSourceCard />}
        dataSourcePill={<GenericTag text="Honolulu County" />}
        filters={{
          dateRange: covidPositivityDateRange,
          onDateRangeChange: setCovidPositivityDateRange,
          tabs: (
            <Tabs
              value={dataViewMode}
              onChange={(_, value: DataViewMode) => setDataViewMode(value)}
            >
              <Tab label="Weekly" value="weekly" />
              <Tab label="7-day rolling average" value="rolling_avg" />
            </Tabs>
          ),
        }}
      >
        <TimeSeriesChart
          dataset={dataset}
          dates={dates}
          series={filteredSeries}
          legendItems={filteredLegendItems}
          yAxisLabel="PERCENT TEST POSITIVITY"
          renderLegend={() => null}
          renderTooltip={hiddenIds => (
            <HawaiiCovidTooltipBody
              rawData={hawaiiCovidData}
              hiddenIds={hiddenIds}
              itemIds={[
                dataViewMode === "weekly"
                  ? POSITIVITY_WEEKLY_ID
                  : POSITIVITY_ROLLING_AVG_ID,
              ]}
            />
          )}
        />
      </ChartWithFilters>
    )
  }, [
    covidPositivityDateRange,
    dataViewMode,
    dataset,
    dates,
    filteredSeries,
    filteredLegendItems,
    hawaiiCovidData,
  ])
  return <Box>{isError ? errorState : loadingState ? loadingState : chart}</Box>
}

const HDOHDataSourceCard = () => (
  <DataSourceCard
    sourceName="Hawaii COVID-19 Cases and Testing"
    dataOwner="HDOH DOCD"
    description="The Hawaii Department of Health’s (HDOH) Disease Outbreak Control Division (DOCD) provides daily COVID-19 test and case count data for each of the state’s four largest counties."
    sourceURL="https://github.com/HDOH-DOCD-data/covid_public_data/blob/main/CSVDownloadepicurve.csv"
  />
)

export const HawaiiCovidTimeSeries = ({ county }: { county: string }) => {
  return (
    <Box>
      <HawaiiCovidPositivityChart county={county} />
      <HawaiiCovidCasesChart county={county} />
    </Box>
  )
}
